import React, { useEffect, useState, useCallback } from "react";
import { attendanceViewApi, errorMessage } from "../adminApi";
import { Card, Table, Badge, Button, Loading, ErrorState, PageHeader, Pagination, Avatar, StatCard } from "../components/ui";
import { Icon } from "../components/icons";
import AttendanceViewModal, { ATTENDANCE_SOURCE, useAttendanceDetail } from "../components/AttendanceDetailModal";
import { formatDate, formatTime, formatMinutes, attendanceFlagColor, attendanceStatusText } from "../format";

/**
 * TAMPILAN LIST ABSENSI — kartu ringkasan (dashboard singkat) + tabel riwayat.
 * Default rentang: periode berjalan (tgl 25 s/d hari ini, cut-off payroll 25–24).
 */

const PAGE_SIZE = 20;

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
/** Awal periode absensi berjalan (tgl 25). */
function periodStart() {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth() - (d.getDate() >= 25 ? 0 : 1), 25));
}
function today() {
  return ymd(new Date());
}
const shortDate = (k) =>
  new Date(`${k}T00:00:00Z`).toLocaleDateString("id-ID", { timeZone: "UTC", day: "numeric", month: "short" });
const weekday = (v) => {
  const d = new Date(v);
  return isNaN(d) ? "" : d.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", weekday: "long" });
};
/** % perubahan cur vs prev (null bila prev 0 → tak bermakna). */
function pctChange(cur, prev) {
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 100);
}
const pill = "h-10 rounded-full bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

/** Parameter tanggal efektif: mode "tidak hadir" = satu tanggal saja. */
function dateParams(f) {
  if (f.absent) {
    const day = f.date_to || today();
    return { date_from: day, date_to: day };
  }
  return { date_from: f.date_from || undefined, date_to: f.date_to || undefined };
}

export default function AttendanceListView({ toggle }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ date_from: periodStart(), date_to: today(), search: "", absent: false });
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const { selected, detail, open: openDetail, close: closeDetail } = useAttendanceDetail();

  const load = useCallback(async (pageArg, f) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: pageArg * PAGE_SIZE, ...dateParams(f) };
      if (f.absent) params.include_absent = 1;
      if (f.search && f.search.trim()) params.search = f.search.trim();
      const res = await attendanceViewApi.list(params);
      setRows(res.rows || []);
      setTotal(res.total ?? (res.rows || []).length);
      setPage(pageArg);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Ringkasan hanya dimuat ulang saat filter diterapkan (bukan saat ganti halaman).
  const loadSummary = useCallback(async (f) => {
    const dp = dateParams(f);
    if (!dp.date_from || !dp.date_to) return setSummary(null);
    setSummaryLoading(true);
    try {
      const params = { ...dp };
      if (f.search && f.search.trim()) params.search = f.search.trim();
      setSummary(await attendanceViewApi.summary(params));
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const apply = (f) => {
    load(0, f);
    loadSummary(f);
  };

  useEffect(() => {
    apply(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setelah absensi dihapus: tutup modal & muat ulang (mundur satu halaman bila halaman ini jadi kosong).
  const handleDeleted = () => {
    closeDetail();
    load(rows.length <= 1 && page > 0 ? page - 1 : page, filters);
    loadSummary(filters);
  };

  const columns = [
    {
      key: "emp",
      header: "Karyawan",
      render: (r) => (
        <div className="flex items-center gap-3 min-w-[220px]">
          <Avatar name={r.employee_name || r.username} size={34} />
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 leading-tight">{r.employee_name || r.username}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {r.nik || r.username}
              {r.job_title ? <span> · {r.job_title}</span> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "date",
      header: "Tanggal",
      render: (r) => (
        <div className="whitespace-nowrap">
          <div className="text-slate-700 font-medium">{formatDate(r.date)}</div>
          <div className="text-[11px] text-slate-400 capitalize">{weekday(r.date)}</div>
        </div>
      ),
    },
    {
      key: "dept",
      header: "Departemen",
      render: (r) => <span className="text-[12px] text-slate-500">{r.department || "-"}</span>,
    },
    {
      key: "mode",
      header: "Mode",
      render: (r) =>
        r.work_mode ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 capitalize">
            {String(r.work_mode).toLowerCase() === "wfh" ? <Icon.Home size={12} /> : <Icon.Building size={12} />}
            {r.work_mode}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "time",
      header: "Masuk → Keluar",
      render: (r) =>
        r.is_absent ? (
          <span className="text-slate-300">—</span>
        ) : (
          <div className="whitespace-nowrap">
            <div className="text-slate-700 font-medium tabular-nums">
              {formatTime(r.check_in)} <span className="text-slate-300">→</span>{" "}
              {r.check_out ? formatTime(r.check_out) : <span className="text-slate-400 font-normal">belum</span>}
            </div>
            {r.attendance_source && r.attendance_source !== "platform" && (
              <div className="text-[10px] text-slate-400">{ATTENDANCE_SOURCE[r.attendance_source] || r.attendance_source}</div>
            )}
          </div>
        ),
    },
    {
      key: "worked",
      header: "Durasi",
      render: (r) => <span className="text-slate-600 tabular-nums whitespace-nowrap">{formatMinutes(r.worked_minutes)}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge color={attendanceFlagColor(r.status)}>{attendanceStatusText(r.status)}</Badge>
          {r.late_minutes > 0 && <span className="text-[11px] text-[#a76a2e]">+{formatMinutes(r.late_minutes)}</span>}
          {r.flags?.needs_hr_review && r.flags?.review_status !== "resolved" && <Badge color="red">Review HR</Badge>}
        </div>
      ),
    },
    {
      key: "act",
      header: "",
      cellClassName: "w-8",
      render: (r) => (r.is_absent ? null : <Icon.ArrowRight size={16} className="text-slate-300" />),
    },
  ];

  const setF = (k) => (e) => setFilters((prev) => ({ ...prev, [k]: e.target.value }));
  const isDirty = filters.search || filters.absent || filters.date_from !== periodStart() || filters.date_to !== today();

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Absensi"
        subtitle="Riwayat absensi (check-in/out, lokasi, foto) — hanya data bawahan Anda."
        action={
          <div className="flex items-center gap-2">
            {toggle}
            <Button variant="secondary" size="sm" onClick={() => apply(filters)}>
              <Icon.Refresh size={15} /> Refresh
            </Button>
          </div>
        }
      />

      <SummaryCards summary={summary} loading={summaryLoading} />

      {/* Toolbar filter (gaya pill, sama dengan kalender) — query jalan saat klik Cari */}
      <div className="flex flex-wrap items-center gap-2.5 mb-3 shrink-0">
        <div className={`${pill} flex items-center gap-2 px-3.5 flex-1 min-w-[220px] max-w-md`}>
          <Icon.Search size={16} className="text-slate-400" />
          <input
            type="text"
            value={filters.search}
            onChange={setF("search")}
            onKeyDown={(e) => e.key === "Enter" && apply(filters)}
            placeholder="Cari nama / NIK / badge / username…"
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          />
        </div>

        <div className={`${pill} flex items-center gap-1.5 px-3.5`}>
          <Icon.Calendar size={15} className="text-slate-400" />
          {!filters.absent && (
            <>
              <input type="date" value={filters.date_from} onChange={setF("date_from")} className="text-[13px] font-medium text-slate-700 bg-transparent focus:outline-none" />
              <span className="text-slate-300">–</span>
            </>
          )}
          <input type="date" value={filters.date_to} onChange={setF("date_to")} className="text-[13px] font-medium text-slate-700 bg-transparent focus:outline-none" />
        </div>

        <button
          onClick={() => setFilters((f) => ({ ...f, absent: !f.absent }))}
          className={`h-10 rounded-full border px-3.5 inline-flex items-center gap-2 text-[13px] font-medium transition ${
            filters.absent ? "bg-[#fce3e5] border-[#f3c3ca] text-[#8f4657]" : "bg-white border-slate-200/80 text-slate-500 hover:text-slate-800"
          }`}
          title="Tampilkan juga karyawan yang tidak hadir (hanya untuk satu tanggal)"
        >
          <span className={`h-2 w-2 rounded-full ${filters.absent ? "bg-[#e0566b]" : "bg-slate-300"}`} />
          Termasuk yang tidak hadir
        </button>

        <button
          onClick={() => apply(filters)}
          className="h-10 px-5 rounded-full bg-brand-600 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-sm hover:bg-brand-700 transition"
        >
          <Icon.Search size={15} /> Cari
        </button>
        {isDirty && (
          <button
            onClick={() => {
              const reset = { date_from: periodStart(), date_to: today(), search: "", absent: false };
              setFilters(reset);
              apply(reset);
            }}
            className="text-[13px] font-medium text-slate-500 hover:text-brand-700 px-2"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(page, filters)} />
        ) : (
          <>
            <Card className="flex-1 min-h-0 overflow-hidden">
              <Table
                columns={columns}
                rows={rows}
                onRowClick={(r) => !r.is_absent && openDetail(r)}
                empty="Tidak ada data absensi pada rentang ini."
              />
            </Card>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={(p) => load(p, filters)} />
          </>
        )}
      </div>

      <AttendanceViewModal open={!!selected} row={selected} detail={detail} onClose={closeDetail} onDeleted={handleDeleted} />
    </div>
  );
}

/** Dashboard singkat di atas list: 4 kartu + tren vs periode sebelumnya (panjang sama). */
function SummaryCards({ summary, loading }) {
  const c = summary?.current;
  const p = summary?.previous;
  const vs = p ? `vs ${shortDate(p.date_from)} – ${shortDate(p.date_to)}` : "";
  const onTimePct = (s) => (s && s.total ? Math.round((s.on_time / s.total) * 100) : null);
  const curPct = onTimePct(c);
  const prevPct = onTimePct(p);

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4 shrink-0">
      <StatCard
        title="Total Kehadiran"
        icon={Icon.Attendance}
        loading={loading}
        value={c?.total ?? "-"}
        unit="record"
        delta={c && p ? pctChange(c.total, p.total) : null}
        description={c ? `${c.employees} karyawan · rata-rata kerja ${formatMinutes(c.avg_worked_minutes)} · ${vs}` : "Tidak ada data"}
      />
      <StatCard
        title="Tepat Waktu"
        icon={Icon.Check}
        loading={loading}
        value={curPct ?? "-"}
        unit={curPct != null ? "%" : undefined}
        delta={curPct != null && prevPct != null ? curPct - prevPct : null}
        deltaSuffix=" pt"
        description={c ? `${c.on_time} dari ${c.total} record hadir normal / WFH · ${vs}` : "Tidak ada data"}
      />
      <StatCard
        title="Telat"
        icon={Icon.Clock}
        loading={loading}
        value={c?.late ?? "-"}
        unit="kali"
        goodWhenUp={false}
        delta={c && p ? pctChange(c.late, p.late) : null}
        description={c ? `Rata-rata telat ${formatMinutes(c.avg_late_minutes)} · ${c.partial} half day / pulang awal` : "Tidak ada data"}
      />
      <StatCard
        title="Anomali"
        icon={Icon.Alert}
        loading={loading}
        value={c?.anomaly ?? "-"}
        unit="record"
        goodWhenUp={false}
        delta={c && p ? pctChange(c.anomaly, p.anomaly) : null}
        description={c ? `${c.needs_review} perlu review HR · lupa check-in/out & double` : "Tidak ada data"}
      />
    </div>
  );
}
